import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';

import { api } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';

const Reports = () => {
  const [period, setPeriod] = useState('monthly');
  const [reportType, setReportType] = useState('applications');

  const { data: reports, isLoading, error } = useQuery(
    ['reports', period, reportType],
    async () => {
      const response = await api.get(`/reports/${reportType}?period=${period}`);
      const data = response.data;
      
      // Data formatting for income/expenses
      if (data.success && data.data && Array.isArray(data.data)) {
        data.data = data.data.map((item: any) => ({
          ...item,
          total_income: item.total_income ? parseFloat(item.total_income) : 0,
          total_expenses: item.total_expenses ? parseFloat(item.total_expenses) : 0,
          count: item.count ? parseInt(item.count) : 0
        }));
      }
      
      return data;
    }
  );

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

  const renderChart = () => {
    if (!reports?.data) return null;

    const data = reports.data;
    
    // Data validation
    if (!Array.isArray(data) || data.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center h-96 text-gray-500">
          <div className="text-6xl mb-4">📊</div>
          <p className="text-lg font-medium">Veri bulunamadı</p>
          <p className="text-sm">Seçilen tarih aralığında {reportType === 'income' ? 'gelir' : reportType === 'expenses' ? 'gider' : 'veri'} bulunmuyor.</p>
        </div>
      );
    }

    switch (reportType) {
      case 'applications':
      case 'vip-transfers':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="period" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="count" fill="#3B82F6" />
            </BarChart>
          </ResponsiveContainer>
        );

      case 'income':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="period" 
                angle={-45}
                textAnchor="end"
                height={80}
                interval={0}
              />
              <YAxis />
              <Tooltip 
                formatter={(value: any) => [`$${value?.toLocaleString() || 0}`, 'Gelir']}
                labelFormatter={(label) => `Dönem: ${label}`}
              />
              <Bar 
                dataKey="total_income" 
                fill="#10B981" 
                radius={[4, 4, 0, 0]}
                name="Gelir"
              />
            </BarChart>
          </ResponsiveContainer>
        );

      case 'expenses':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="period" 
                angle={-45}
                textAnchor="end"
                height={80}
                interval={0}
              />
              <YAxis />
              <Tooltip 
                formatter={(value: any) => [`$${value?.toLocaleString() || 0}`, 'Gider']}
                labelFormatter={(label) => `Dönem: ${label}`}
              />
              <Bar 
                dataKey="total_expenses" 
                fill="#EF4444" 
                radius={[4, 4, 0, 0]}
                name="Gider"
              />
            </BarChart>
          </ResponsiveContainer>
        );

      default:
        return null;
    }
  };

  const getReportTitle = () => {
    const titles = {
      applications: 'Başvuru Sayıları',
      'vip-transfers': 'VIP Transfer Sayıları',
      income: 'Gelir Raporu',
      expenses: 'Gider Raporu'
    };
    return titles[reportType as keyof typeof titles] || '';
  };

  const getPeriodLabel = () => {
    const labels = {
      daily: 'Günlük',
      weekly: 'Haftalık',
      monthly: 'Aylık'
    };
    return labels[period as keyof typeof labels] || '';
  };

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Raporlar yüklenirken bir hata oluştu.</p>
      </div>
    );
  }

  return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Raporlar</h1>
            <p className="mt-1 text-sm text-gray-500">
              Detaylı analiz ve raporlar
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white shadow rounded-lg p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Report Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Rapor Türü
              </label>
              <select
                value={reportType}
                onChange={(e) => setReportType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="applications">Başvuru Sayıları</option>
                <option value="vip-transfers">VIP Transfer Sayıları</option>
                <option value="income">Gelir Raporu</option>
                <option value="expenses">Gider Raporu</option>
              </select>
            </div>

            {/* Period */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Dönem
              </label>
              <select
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="daily">Günlük</option>
                <option value="weekly">Haftalık</option>
                <option value="monthly">Aylık</option>
              </select>
            </div>
          </div>
        </div>



        {/* Chart */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                {getReportTitle()} - {getPeriodLabel()}
              </h3>
            </div>

            {isLoading ? (
              <div className="flex justify-center py-12">
                <LoadingSpinner size="lg" />
              </div>
            ) : (
              <div className="h-96">
                {renderChart()}
              </div>
            )}
          </div>
        </div>

        {/* Data Table */}
        {reports?.data && (
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                Detaylı Veriler
              </h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Dönem
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {reportType === 'income' || reportType === 'expenses' ? 'Tutar' : 'Sayı'}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {reports.data.map((item: any, index: number) => (
                      <tr key={index}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {item.period}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {reportType === 'income' 
                            ? `$${item.total_income?.toLocaleString() || 0}`
                            : reportType === 'expenses'
                            ? `$${item.total_expenses?.toLocaleString() || 0}`
                            : item.count
                          }
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
  );
};

export default Reports;
